import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Empty, Input, InputNumber, Spin } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useToast } from '@/contexts/ToastContext'
import { useAuthSession } from '@/lib/session'
import { StaffAssignedDataPoint } from '../../types'
import {
  useAllocations,
  useAllocationCandidates,
  useReplaceJobAllocations,
  useRemoveSourceAllocationStaff,
  useSaveAllocations,
  useSaveJobBudgets,
  useSourceAllocationStaff,
  useStaffAvailability,
  useValidateSourceAllocationStaffRemoval,
} from '../../hooks'
import type {
  AllocationJob,
  AllocationStatus,
  CapacitySettings,
  JobAllocation,
  SaveAllocationPayload,
  SaveJobBudgetPayload,
  SourceAllocationStaff,
  StaffAvailability,
} from '../../types'
import {
  buildPeriodStarts,
  formatAllocationPeriodLabel,
  getPeriodRangeEnd,
  getPeriodStartIso,
  getPeriodUnit,
  getWeeksInPeriod,
  normalizePeriodStartIso,
} from '../../utils'
import type { Granularity } from '../../types'
import './AllocationTab.css'
import { formatShortDate } from './workflowUtils'

const PERIOD_COUNT = 3
const MIN_PERIOD_COUNT = 1
const ALLOCATION_RANGE_START = '1900-01-01'
const ALLOCATION_RANGE_END = '9999-12-31'
const SOURCE_STAFF_ASSIGNED_TO_TASK_REMOVAL_MESSAGE =
  'This process cannot push through because the staff being removed is assigned to tasks under this job.'
const SINGLE_MANAGER_MESSAGE =
  'A job can only have one manager. Remove or replace the existing manager before assigning another.'
const FYI_MANAGER_REQUIRED_MESSAGE =
  'An FYI job must have a manager. Assign a replacement manager before removing the current one.'

interface Props {
  settings: CapacitySettings
}

type SingleModal = {
  mode: 'create' | 'edit'
  jobId: string
  staffIds: string[]
  sourceStaffIds: string[]
  staffDraft: string
  grid: Record<string, Record<string, number>>
  existingGrid: Record<string, Record<string, number>>
  notes: string
  initialNotes: string
  periodStart: string
  periodCount: number
  replaceWeekStart: string
}

type BulkModal = {
  jobIds: string[]
  staffId: string
  grid: Record<string, Record<string, number>>
  hadExistingAllocations: boolean
  periodStart: string
  periodCount: number
}

type BudgetReviewModal = {
  jobIds: string[]
}

type BudgetChange = {
  job: AllocationJob
  currentValue: number
  newValue: number
}

export function AllocationTab({ settings }: Props) {
  const session = useAuthSession()
  const clientId = session?.currentClientId ?? ''
  const { addToast } = useToast()
  const granularity = settings.defaultGranularity
  const dollarMode = settings.budgetMode === 'dollars'
  const metricShortLabel = dollarMode ? 'amount' : 'hours'
  const metricInputLabel = dollarMode ? 'Amount' : 'Hours'
  const isXpmJobManager = settings.staffAssignedDataPoint === StaffAssignedDataPoint.XpmJobManager
  const isFyiJobManager = settings.staffAssignedDataPoint === StaffAssignedDataPoint.FyiJobManager
  const isSingleManager = isXpmJobManager || isFyiJobManager

  const [statusFilter, setStatusFilter] = useState<AllocationStatus | 'all'>('all')
  const [stateFilter, setStateFilter] = useState('all')
  const [inputValue, setInputValue] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 25
  const [planningValues, setPlanningValues] = useState<Record<string, number>>({})
  const [savedBudgetValues, setSavedBudgetValues] = useState<Record<string, number>>({})
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([])
  const [rangeStart] = useState(() => getPeriodStartIso(new Date(), granularity))
  const [singleModal, setSingleModal] = useState<SingleModal | null>(null)
  const [bulkModal, setBulkModal] = useState<BulkModal | null>(null)
  const [isSingleAllocationSaving, setIsSingleAllocationSaving] = useState(false)
  const [isBulkAllocationSaving, setIsBulkAllocationSaving] = useState(false)
  const isSingleAllocationSavingRef = useRef(false)
  const isBulkAllocationSavingRef = useRef(false)
  const [sourcePrefillWarningJobId, setSourcePrefillWarningJobId] = useState<string | null>(null)
  const [budgetReviewModal, setBudgetReviewModal] = useState<BudgetReviewModal | null>(null)
  const [budgetReviewError, setBudgetReviewError] = useState<string | null>(null)

  const requestedPeriodCount = Math.max(
    PERIOD_COUNT + 5,
    singleModal?.periodCount ?? 0,
    bulkModal?.periodCount ?? 0
  )
  const modalPeriodStart = [singleModal?.periodStart, bulkModal?.periodStart]
    .filter((periodStart): periodStart is string => Boolean(periodStart))
    .sort()[0]
  const availabilityRangeStart = modalPeriodStart && modalPeriodStart < rangeStart ? modalPeriodStart : rangeStart
  const defaultRangeEnd = getPeriodRangeEnd(rangeStart, requestedPeriodCount, granularity)
  const singleModalRangeEnd = singleModal
    ? getPeriodRangeEnd(singleModal.periodStart, singleModal.periodCount, granularity)
    : defaultRangeEnd
  const bulkModalRangeEnd = bulkModal
    ? getPeriodRangeEnd(bulkModal.periodStart, bulkModal.periodCount, granularity)
    : defaultRangeEnd
  const modalRangeEnd = singleModalRangeEnd > bulkModalRangeEnd ? singleModalRangeEnd : bulkModalRangeEnd
  const rangeEnd = modalRangeEnd > defaultRangeEnd ? modalRangeEnd : defaultRangeEnd

  const candidatesQuery = useAllocationCandidates(clientId, {
    allocationStatus: statusFilter === 'all' ? undefined : statusFilter,
    state: stateFilter === 'all' ? undefined : stateFilter,
    search: search.trim() || undefined,
    page,
    pageSize,
  })
  const availabilityQuery = useStaffAvailability(clientId, availabilityRangeStart, rangeEnd, {
    page: 1,
    pageSize: -1,
  })
  const existingAllocationsQuery = useAllocations(clientId, ALLOCATION_RANGE_START, ALLOCATION_RANGE_END, {
    search: search.trim() || undefined,
    page: 1,
    pageSize: -1,
  })
  const saveMutation = useSaveAllocations()
  const replaceMutation = useReplaceJobAllocations()
  const removeSourceStaffMutation = useRemoveSourceAllocationStaff()
  const validateSourceStaffRemovalMutation = useValidateSourceAllocationStaffRemoval()
  const saveBudgetsMutation = useSaveJobBudgets()

  const jobs = candidatesQuery.data?.items ?? []
  const visibleBudgetValues = useMemo(
    () => ({ ...savedBudgetValues, ...planningValues }),
    [planningValues, savedBudgetValues]
  )
  const changedBudgets = useMemo(
    () => getChangedBudgets(jobs, planningValues, savedBudgetValues, dollarMode),
    [jobs, planningValues, savedBudgetValues, dollarMode]
  )
  const changedBudgetByJobId = useMemo(
    () => changedBudgets.reduce<Record<string, BudgetChange>>((acc, change) => {
      acc[change.job.jobId] = change
      return acc
    }, {}),
    [changedBudgets]
  )
  const reviewBudgetChanges = useMemo(
    () => budgetReviewModal
      ? budgetReviewModal.jobIds
        .map((jobId) => changedBudgetByJobId[jobId])
        .filter((change): change is BudgetChange => Boolean(change))
      : [],
    [budgetReviewModal, changedBudgetByJobId]
  )
  const isBudgetReviewSaving = saveBudgetsMutation.isPending
  const totalJobs = candidatesQuery.data?.totalCount ?? jobs.length
  const totalPages = Math.max(1, Math.ceil(totalJobs / pageSize))
  const stateOptions = useMemo(() => {
    return ['all', ...(candidatesQuery.data?.stateOptions ?? [])]
  }, [candidatesQuery.data?.stateOptions])
  const availabilityItems = useMemo(
    () => availabilityQuery.data?.items ?? [],
    [availabilityQuery.data?.items]
  )
  const availabilityByPeriod = useMemo(
    () => buildAvailabilityByPeriod(availabilityItems),
    [availabilityItems]
  )
  const staffDirectory = useMemo(
    () => summarizeStaffDirectory(availabilityItems),
    [availabilityItems]
  )
  const existingAllocations = useMemo(
    () => existingAllocationsQuery.data?.items ?? [],
    [existingAllocationsQuery.data?.items]
  )
  const singleModalJobId = singleModal?.jobId ?? ''
  const sourceStaffJobId = singleModalJobId
  const sourceStaffQuery = useSourceAllocationStaff(clientId, sourceStaffJobId)
  const sourceAllocationStaff = useMemo(
    () => sourceStaffQuery.data ?? [],
    [sourceStaffQuery.data]
  )
  const sourceAllocationStaffIds = useMemo(
    () => sourceAllocationStaff.map((staff) => staff.staffId).filter(Boolean),
    [sourceAllocationStaff]
  )
  const sourceAllocationStaffById = useMemo(
    () => sourceAllocationStaff.reduce<Record<string, SourceAllocationStaff>>((acc, staff) => {
      if (staff.staffId) {
        acc[staff.staffId] = staff
      }
      return acc
    }, {}),
    [sourceAllocationStaff]
  )
  const allocationGroups = useMemo(
    () => groupAllocationsByJob(existingAllocations, granularity, dollarMode),
    [existingAllocations, granularity, dollarMode]
  )
  const allocationGroupByJobId = useMemo(
    () => allocationGroups.reduce<Record<string, AllocationJobGroup>>((acc, group) => {
      acc[group.jobId] = group
      return acc
    }, {}),
    [allocationGroups]
  )
  const selectableStaffDirectory = useMemo(
    () => mergeSelectableStaffDirectory(staffDirectory, sourceAllocationStaff),
    [staffDirectory, sourceAllocationStaff]
  )
  const displayStaffDirectory = useMemo(
    () => mergeAllocationDisplayDirectory(selectableStaffDirectory, existingAllocations),
    [selectableStaffDirectory, existingAllocations]
  )

  const isLoading = candidatesQuery.isLoading || availabilityQuery.isLoading || existingAllocationsQuery.isLoading
  const isError = candidatesQuery.isError || availabilityQuery.isError || existingAllocationsQuery.isError

  const visibleJobIds = jobs.map((job) => job.jobId)
  const selectedVisibleJobIds = selectedJobIds.filter((jobId) => visibleJobIds.includes(jobId))
  const selectedCount = selectedVisibleJobIds.length
  const allSelected = visibleJobIds.length > 0 && visibleJobIds.every((jobId) => selectedJobIds.includes(jobId))
  const periodUnit = getPeriodUnit(granularity)
  const hasInvalidBudgetChanges = changedBudgets.some((change) => change.newValue <= 0)
  const isSaveBudgetDisabled = changedBudgets.length === 0 || hasInvalidBudgetChanges || isBudgetReviewSaving
  const isBulkAllocateDisabled = selectedCount < 2
  const hasPendingHeaderActions = changedBudgets.length > 0 || selectedCount >= 2

  useEffect(() => {
    if (!singleModalJobId || sourceAllocationStaffIds.length === 0) return

    setSingleModal((prev) => {
      if (!prev || prev.jobId !== sourceStaffJobId) return prev

      const mergedStaffIds = Array.from(new Set([...prev.staffIds, ...sourceAllocationStaffIds]))
      const mergedSourceStaffIds = Array.from(new Set([...prev.sourceStaffIds, ...sourceAllocationStaffIds]))
      if (mergedStaffIds.length === prev.staffIds.length && mergedSourceStaffIds.length === prev.sourceStaffIds.length) return prev

      return { ...prev, staffIds: mergedStaffIds, sourceStaffIds: mergedSourceStaffIds }
    })
  }, [singleModalJobId, sourceStaffJobId, sourceAllocationStaffIds])

  useEffect(() => {
    if (!singleModal || !sourceStaffQuery.isError || sourcePrefillWarningJobId === singleModal.jobId) return

    addToast('Source-assigned staff could not be loaded. You can still allocate manually.', 'warning')
    setSourcePrefillWarningJobId(singleModal.jobId)
  }, [addToast, singleModal, sourcePrefillWarningJobId, sourceStaffQuery.isError])

  useEffect(() => {
    if (Object.keys(savedBudgetValues).length === 0) return

    setSavedBudgetValues((prev) => {
      let changed = false
      const next = { ...prev }
      for (const job of jobs) {
        const savedValue = prev[job.jobId]
        if (savedValue == null) continue

        const serverValue = Number(getBudgetValue(job, dollarMode) ?? 0)
        if (Math.abs(savedValue - serverValue) <= 0.0005) {
          delete next[job.jobId]
          changed = true
        }
      }

      return changed ? next : prev
    })
  }, [dollarMode, jobs, savedBudgetValues])

  const toggleJob = (jobId: string, checked: boolean) => {
    setSelectedJobIds(checked
      ? Array.from(new Set([...selectedJobIds, jobId]))
      : selectedJobIds.filter((id) => id !== jobId))
  }

  const openSingleModal = (jobId: string) => {
    const job = jobs.find((j) => j.jobId === jobId)
    if (getTargetValue(job, visibleBudgetValues[jobId], dollarMode) <= 0) {
      addToast(`Add budget to jobs before allocating.`, 'warning')
      return
    }
    setSingleModal({
      mode: 'create',
      jobId,
      staffIds: [],
      sourceStaffIds: [],
      staffDraft: '',
      grid: {},
      existingGrid: {},
      notes: job?.notes ?? '',
      initialNotes: job?.notes ?? '',
      periodStart: rangeStart,
      periodCount: PERIOD_COUNT,
      replaceWeekStart: rangeStart,
    })
  }

  const openEditModal = (group: AllocationJobGroup) => {
    const periodStarts = groupAllocationsByStaffPeriod(group.allocations, granularity, dollarMode)
    const staffIds = Object.keys(periodStarts)
    const notes = jobs.find((job) => job.jobId === group.jobId)?.notes ?? ''
    const lastPeriod = group.periodStarts[group.periodStarts.length - 1] ?? rangeStart
    const periodStart = rangeStart
    const periodEnd = lastPeriod > periodStart ? lastPeriod : periodStart
    const periodCount = getInclusivePeriodCount(periodStart, periodEnd, granularity)

    setSingleModal({
      mode: 'edit',
      jobId: group.jobId,
      staffIds,
      sourceStaffIds: [],
      staffDraft: '',
      grid: periodStarts,
      existingGrid: periodStarts,
      notes,
      initialNotes: notes,
      periodStart,
      periodCount,
      replaceWeekStart: periodStart,
    })
  }

  const openBulkModal = () => {
    if (selectedCount < 2) {
      addToast('Select at least two jobs to bulk allocate.', 'warning')
      return
    }
    setBulkModal({
      jobIds: selectedVisibleJobIds,
      staffId: '',
      grid: {},
      hadExistingAllocations: false,
      periodStart: rangeStart,
      periodCount: PERIOD_COUNT,
    })
  }

  const loadBulkStaffAllocations = (modal: BulkModal, staffId: string): BulkModal => {
    if (!staffId) {
      return {
        ...modal,
        staffId: '',
        grid: {},
        hadExistingAllocations: false,
        periodStart: rangeStart,
        periodCount: PERIOD_COUNT,
      }
    }

    const selectedBulkJobIds = new Set(modal.jobIds)
    const staffAllocations = existingAllocations.filter((allocation) =>
      allocation.staffId === staffId && selectedBulkJobIds.has(allocation.jobId)
    )
    const existingPeriods = Array.from(new Set(
      staffAllocations.map((allocation) => normalizePeriodStartIso(allocation.weekStart, granularity))
    )).sort()
    const defaultPeriodEnd = getPeriodRangeEnd(rangeStart, PERIOD_COUNT, granularity)
    const firstExistingPeriod = existingPeriods[0]
    const lastExistingPeriod = existingPeriods[existingPeriods.length - 1]
    const periodStart = firstExistingPeriod && firstExistingPeriod < rangeStart ? firstExistingPeriod : rangeStart
    const periodEnd = lastExistingPeriod && lastExistingPeriod > defaultPeriodEnd ? lastExistingPeriod : defaultPeriodEnd

    return {
      ...modal,
      staffId,
      grid: groupAllocationsByJobPeriod(staffAllocations, granularity, dollarMode),
      hadExistingAllocations: staffAllocations.length > 0,
      periodStart,
      periodCount: getInclusivePeriodCount(periodStart, periodEnd, granularity),
    }
  }

  const refetchAll = () => {
    void candidatesQuery.refetch()
    void availabilityQuery.refetch()
    void existingAllocationsQuery.refetch()
  }

  const handleSearch = () => {
    setSearch(inputValue)
    setPage(1)
  }

  const cancelPendingHeaderActions = () => {
    setSelectedJobIds([])
    setPlanningValues({})
    setBudgetReviewError(null)
  }

  const closeSingleModal = () => {
    if (isSingleAllocationSavingRef.current) return
    setSingleModal(null)
  }

  const closeBulkModal = () => {
    if (isBulkAllocationSavingRef.current) return
    setBulkModal(null)
  }

  const closeBudgetReviewModal = () => {
    if (isBudgetReviewSaving) return
    setBudgetReviewModal(null)
    setBudgetReviewError(null)
  }

  const saveSingle = async () => {
    if (!singleModal || isSingleAllocationSavingRef.current) return
    const selectedJob = jobs.find((job) => job.jobId === singleModal.jobId)
    const jobNote = singleModal.notes !== singleModal.initialNotes && selectedJob
      ? {
          jobId: selectedJob.budgetJobId ?? selectedJob.jobId,
          notes: singleModal.notes.trim() || null,
        }
      : undefined
    if (isSingleManager && new Set(singleModal.staffIds).size > 1) {
      addToast(SINGLE_MANAGER_MESSAGE, 'error')
      return
    }
    const periods = buildPeriodStarts(singleModal.periodStart, singleModal.periodCount, granularity)
    const validation = getSingleModalValidation(singleModal, periods, availabilityByPeriod, dollarMode)
    if (validation.hasInvalid) {
      addToast('One or more allocation entries exceed available capacity.', 'warning')
    }
    const allocations = singleModal.staffIds.flatMap((staffId) =>
      buildPeriodAllocationEntries(periods, staffId, singleModal.grid, granularity)
        .map((entry) => ({
          jobId: singleModal.jobId,
          staffId,
          weekStart: entry.weekStart,
          allocatedHours: dollarMode ? undefined : entry.allocatedValue,
          allocatedAmount: dollarMode ? entry.allocatedValue : undefined,
        }))
    )
    const removedSourceStaffIds = singleModal.sourceStaffIds.filter((staffId) =>
      !singleModal.staffIds.includes(staffId)
      && !Object.prototype.hasOwnProperty.call(singleModal.existingGrid, staffId)
    )
    const isManagerSourceReplacement =
      isSingleManager && removedSourceStaffIds.length > 0 && allocations.length > 0
    const sourceRemovalHandledByManagerReplacement =
      isSingleManager && (singleModal.mode === 'edit' || isManagerSourceReplacement) && allocations.length > 0

    if (isFyiJobManager && removedSourceStaffIds.length > 0 && allocations.length === 0) {
      addToast(FYI_MANAGER_REQUIRED_MESSAGE, 'error')
      return
    }
    if (isFyiJobManager && singleModal.mode === 'edit' && singleModal.staffIds.length === 0) {
      addToast(FYI_MANAGER_REQUIRED_MESSAGE, 'error')
      return
    }
    if (allocations.length === 0 && singleModal.mode === 'create' && removedSourceStaffIds.length === 0) {
      addToast(`Enter at least one ${metricShortLabel} to allocate.`, 'warning')
      return
    }
    isSingleAllocationSavingRef.current = true
    setIsSingleAllocationSaving(true)
    try {
      if (removedSourceStaffIds.length > 0) {
        await validateSourceStaffRemovalMutation.mutateAsync({
          clientId,
          payload: {
            jobId: singleModal.jobId,
            staffIds: removedSourceStaffIds,
          },
        })
      }
      if (singleModal.mode === 'edit' || isManagerSourceReplacement) {
        await replaceMutation.mutateAsync({
          clientId,
          payload: {
            jobId: singleModal.jobId,
            weekStart: singleModal.replaceWeekStart,
            weekEnd: getPeriodRangeEnd(singleModal.periodStart, singleModal.periodCount, granularity),
            allocations,
            jobNote,
          },
        })
        addToast('Allocation changes saved successfully.', 'success')
      } else if (allocations.length > 0) {
        await saveMutation.mutateAsync({ clientId, allocations, options: { jobNote } })
        addToast('Allocations saved successfully.', 'success')
      } else if (jobNote) {
        await saveMutation.mutateAsync({ clientId, allocations: [], options: { jobNote } })
      }
      if (!sourceRemovalHandledByManagerReplacement) {
        for (const staffId of removedSourceStaffIds) {
          await removeSourceStaffMutation.mutateAsync({
            clientId,
            payload: {
              jobId: singleModal.jobId,
              staffId,
            },
          })
        }
      }
      if (allocations.length === 0 && removedSourceStaffIds.length > 0) {
        addToast('Source staff removal saved successfully.', 'success')
      } else if (removedSourceStaffIds.length > 0) {
        addToast('Source staff removal queued successfully.', 'success')
      }
      setSingleModal(null)
      refetchAll()
    } catch (error) {
      if (isSourceStaffAssignedToTaskRemovalError(error)) {
        setSingleModal((prev) => {
          if (!prev || prev.jobId !== singleModal.jobId) return prev
          return {
            ...prev,
            staffIds: Array.from(new Set([...prev.staffIds, ...removedSourceStaffIds])),
          }
        })
        addToast(SOURCE_STAFF_ASSIGNED_TO_TASK_REMOVAL_MESSAGE, 'warning')
        return
      }
      addToast(error instanceof Error ? error.message : 'Failed to save allocations.', 'error')
    } finally {
      isSingleAllocationSavingRef.current = false
      setIsSingleAllocationSaving(false)
    }
  }

  const saveBulk = async () => {
    if (!bulkModal || isBulkAllocationSavingRef.current) return
    if (!bulkModal.staffId) {
      addToast('Select a staff member before saving.', 'warning')
      return
    }
    if (isSingleManager) {
      const conflictingJobIds = bulkModal.jobIds.filter((jobId) => {
        const knownStaffIds = [
          ...(jobs.find((job) => job.jobId === jobId)?.staff ?? []).map((staff) => staff.staffId),
          ...existingAllocations.filter((allocation) => allocation.jobId === jobId).map((allocation) => allocation.staffId),
        ]
        return knownStaffIds.some((knownStaffId) =>
          knownStaffId && knownStaffId !== bulkModal.staffId)
      })
      if (conflictingJobIds.length > 0) {
        addToast(SINGLE_MANAGER_MESSAGE, 'error')
        return
      }
    }
    const unavailableJobIds = bulkModal.jobIds.filter((jobId) => !jobs.some((job) => job.jobId === jobId))
    if (unavailableJobIds.length > 0) {
      addToast('One or more selected jobs are no longer available. Refresh the selection and try again.', 'warning')
      setBulkModal(null)
      refetchAll()
      return
    }
    const periods = buildPeriodStarts(bulkModal.periodStart, bulkModal.periodCount, granularity)
    const validation = getBulkModalValidation(bulkModal, periods, availabilityByPeriod, dollarMode)
    if (validation.hasInvalid) {
      addToast('One or more allocation entries exceed available capacity.', 'warning')
    }
    const allocations = buildBulkAllocationPayload(bulkModal, periods, granularity, dollarMode)
    if (allocations.length === 0 && !bulkModal.hadExistingAllocations) {
      addToast(`Enter at least one ${metricShortLabel} to allocate.`, 'warning')
      return
    }
    isBulkAllocationSavingRef.current = true
    setIsBulkAllocationSaving(true)
    try {
      await saveMutation.mutateAsync({
        clientId,
        allocations,
        options: {
          replaceStaffAllocations: true,
          staffId: bulkModal.staffId,
          jobIds: bulkModal.jobIds,
          weekStart: bulkModal.periodStart,
          weekEnd: getPeriodRangeEnd(bulkModal.periodStart, bulkModal.periodCount, granularity),
        },
      })
      addToast('Allocations saved successfully.', 'success')
      setSelectedJobIds((prev) => prev.filter((jobId) => !bulkModal.jobIds.includes(jobId)))
      setBulkModal(null)
      refetchAll()
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to save allocations.', 'error')
    } finally {
      isBulkAllocationSavingRef.current = false
      setIsBulkAllocationSaving(false)
    }
  }

  const saveBudgetChangeList = async (budgetChanges: BudgetChange[]) => {
    if (isBudgetReviewSaving || budgetChanges.length === 0) return

    const budgets: SaveJobBudgetPayload[] = budgetChanges.map((change) => ({
      jobId: change.job.budgetJobId ?? change.job.jobId,
      budgetMode: settings.budgetMode,
      budgetValue: change.newValue,
    }))

    try {
      await saveBudgetsMutation.mutateAsync({
        clientId,
        budgets,
      })
      addToast(budgetChanges.length === 1 ? 'Budget saved successfully.' : 'Budgets saved successfully.', 'success')
      setSavedBudgetValues((prev) => {
        const next = { ...prev }
        for (const change of budgetChanges) {
          next[change.job.jobId] = change.newValue
        }
        return next
      })
      setPlanningValues((prev) => {
        const next = { ...prev }
        for (const change of budgetChanges) {
          delete next[change.job.jobId]
        }
        return next
      })
      setBudgetReviewModal(null)
      setBudgetReviewError(null)
      refetchAll()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save budgets.'
      setBudgetReviewError(message)
      addToast(message, 'error')
    }
  }

  const saveOrReviewBudgetChanges = () => {
    if (isBudgetReviewSaving) return

    if (changedBudgets.length === 0) {
      addToast('No budget changes to save.', 'warning')
      return
    }

    if (changedBudgets.some((change) => change.newValue <= 0)) {
      addToast(`Enter a positive ${metricShortLabel} budget for every modified job before saving.`, 'warning')
      return
    }

    setBudgetReviewError(null)
    setBudgetReviewModal({ jobIds: changedBudgets.map((change) => change.job.jobId) })
  }

  const saveBudgetChanges = async () => {
    await saveBudgetChangeList(reviewBudgetChanges)
  }

  return (
    <div className="capacity-wireframe allocation-wireframe">
      <div className="filter-bar">
        <span className="filter-bar-label">Status</span>
        <select
          className="filter-select"
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value as AllocationStatus | 'all')
            setPage(1)
          }}
        >
          <option value="all">All</option>
          <option value="no-budget">No Budget</option>
          <option value="unscheduled">Unscheduled</option>
          <option value="unassigned">Unassigned</option>
          <option value="needs-reallocation">Needs Reallocation</option>
          <option value="allocated">Allocated</option>
        </select>
        <div className="filter-divider" />
        <span className="filter-bar-label">State</span>
        <select
          className="filter-select"
          value={stateFilter}
          onChange={(event) => {
            setStateFilter(event.target.value)
            setPage(1)
          }}
        >
          {stateOptions.map((state) => (
            <option key={state} value={state}>{state === 'all' ? 'All States' : state}</option>
          ))}
        </select>
        <div className="filter-divider" />
        <Input
          className="allocation-search"
          placeholder="Search client or job..."
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onPressEnter={handleSearch}
          suffix={<SearchOutlined className="cursor-pointer text-brand-500" onClick={handleSearch} />}
          allowClear
          style={{ width: 220 }}
        />
      </div>

      <main className="capacity-main">
        <div className="section">
          <div className="section-header">
            <span className="section-title">Jobs to Allocate</span>
            <div className="section-actions">
              {hasPendingHeaderActions && (
                <>
                  <Button size="small" onClick={cancelPendingHeaderActions}>
                    Cancel
                  </Button>
                  <div className="filter-divider" />
                </>
              )}
              <Button
                type="primary"
                size="small"
                disabled={isSaveBudgetDisabled}
                loading={isBudgetReviewSaving}
                onClick={saveOrReviewBudgetChanges}
              >
                Save Budget ({changedBudgets.length})
              </Button>
              <Button
                type="primary"
                size="small"
                disabled={isBulkAllocateDisabled}
                onClick={openBulkModal}
              >
                Bulk Allocate ({selectedCount})
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="section-loading-inline">
              <Spin size="large" />
            </div>
          ) : isError ? (
            <div className="section-empty">
              <Alert
                type="error"
                message="Failed to load allocation data"
                action={
                  <Button size="small" onClick={() => {
                    void candidatesQuery.refetch()
                    void availabilityQuery.refetch()
                    void existingAllocationsQuery.refetch()
                  }}>Retry</Button>
                }
              />
            </div>
          ) : jobs.length === 0 ? (
            <div className="allocation-empty"><Empty description="No jobs need allocation" /></div>
          ) : (
            <>
              <div className="table-wrap allocation-table">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={(event) => setSelectedJobIds(event.target.checked ? visibleJobIds : [])}
                        />
                      </th>
                      <th>Client</th>
                      <th className="allocation-job-name">Job</th>
                      <th>Budget</th>
                      <th className="allocation-staff">Staff</th>
                      <th>Deadline</th>
                      <th>Notes</th>
                      <th>Status</th>
                      <th style={{ width: 110 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => {
                      const allocationGroup = allocationGroupByJobId[job.jobId]
                      const isNoBudget = job.allocationStatus === 'no-budget'
                      const budgetValue = visibleBudgetValues[job.jobId] ?? getBudgetValue(job, dollarMode)
                      const savedBudgetValue = savedBudgetValues[job.jobId] ?? getBudgetValue(job, dollarMode)
                      const remainingValue = budgetValue != null && budgetValue !== savedBudgetValue
                        ? Math.max(0, budgetValue - getAllocatedValue(job, dollarMode))
                        : getRemainingValue(job, dollarMode)
                      const rowClass = job.allocationStatus === 'needs-reallocation'
                        ? 'allocation-row-warning'
                        : isNoBudget ? 'allocation-row-muted' : ''
                      const staffNames = job.staff.map((staff) => staff.staffName).filter(Boolean).join(', ') || '-'
                      return (
                        <tr key={job.jobId} className={rowClass}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedJobIds.includes(job.jobId)}
                              onChange={(event) => toggleJob(job.jobId, event.target.checked)}
                            />
                          </td>
                          <td><strong>{job.clientName}</strong></td>
                          <td className="allocation-job-name" title={job.jobName}>{job.jobName}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <InputNumber
                                min={0}
                                size="small"
                                value={budgetValue}
                                placeholder={dollarMode ? 'Set budget $' : 'Set hrs'}
                                formatter={(value) => {
                                  if (value == null) return ''
                                  if (!dollarMode) return String(value)
                                  return formatCurrency(value)
                                }}
                                parser={(value) => {
                                  return parseCurrencyInput(value)
                                }}
                                onChange={(value) => setPlanningValues((prev) => ({ ...prev, [job.jobId]: Number(value ?? 0) }))}
                                className="allocation-hours-input"
                              />
                              {remainingValue != null && remainingValue < (budgetValue ?? 0)
                                ? <span className="allocation-subtle"> ({formatRemainingValue(remainingValue, dollarMode)} left)</span>
                                : null}
                            </div>
                          </td>
                          <td className="allocation-staff" title={staffNames}>{staffNames}</td>
                          <td>{formatShortDate(job.dueDate)}</td>
                          <td title={job.notes ?? undefined}>{job.notes?.trim() || '-'}</td>
                          <td>
                            <span className={`badge ${getAllocationBadge(job.allocationStatus)}`}>
                              {formatAllocationStatus(job.allocationStatus)}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={() => allocationGroup ? openEditModal(allocationGroup) : openSingleModal(job.jobId)}
                            >
                              {allocationGroup ? 'Edit' : 'Allocate'}
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <div className="section-actions capacity-pagination">
                <span className="section-meta">Page {page} of {totalPages} | {totalJobs} jobs</span>
                <div className="filter-divider" />
                <button type="button" className="btn btn-sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
                <button type="button" className="btn btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
              </div>
            </>
          )}
        </div>

      </main>

      {singleModal && (() => {
        const allocationGroup = allocationGroups.find((group) => group.jobId === singleModal.jobId)
        const job = jobs.find((j) => j.jobId === singleModal.jobId) ?? getJobFromAllocationGroup(allocationGroup)
        if (!job) return null
        const periods = buildPeriodStarts(singleModal.periodStart, singleModal.periodCount, granularity)
        const staffSummaries = summarizeStaffAvailabilityForPeriods(selectableStaffDirectory, periods, availabilityByPeriod, dollarMode)
        const staffOptions = staffSummaries.filter(
          (s) => !singleModal.staffIds.includes(s.staffId)
        )
        const selectedStaffDraft = staffOptions.some((s) => s.staffId === singleModal.staffDraft)
          ? singleModal.staffDraft
          : ''
        const validation = getSingleModalValidation(singleModal, periods, availabilityByPeriod, dollarMode)
        const targetValue = getSingleModalTargetValue(singleModal, jobs.find((j) => j.jobId === singleModal.jobId), allocationGroup, visibleBudgetValues[job.jobId], dollarMode)
        const totalAllocated = singleModal.staffIds.reduce(
          (sum, staffId) => sum + periods.reduce((s, period) => s + (singleModal.grid[staffId]?.[period] ?? 0), 0),
          0
        )
        const budgetTolerance = dollarMode ? 0.5 : 0.05
        const isBudgetExceeded = totalAllocated > targetValue + budgetTolerance
        const isSourceStaffLoading = sourceStaffQuery.isFetching
        return (
          <div className="allocation-modal-overlay" onClick={closeSingleModal}>
            <div
              className="allocation-modal"
              style={{ maxWidth: 680 }}
              role="dialog"
              aria-modal="true"
              aria-label="Allocate job"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="allocation-modal-header">
                <div>
                  <div className="allocation-modal-title">{singleModal.mode === 'edit' ? 'Edit Allocation' : 'Allocate'}: {job.clientName}</div>
                  <div className="allocation-modal-subtitle">
                    {job.clientName} - {job.jobName} &middot; {formatMetricValue(targetValue, dollarMode)} &middot; Due {formatDate(job.dueDate)}
                  </div>
                </div>
                <button type="button" className="allocation-modal-close" disabled={isSingleAllocationSaving} onClick={closeSingleModal} aria-label="Close">&times;</button>
              </div>
              <div className="allocation-modal-body">
                <div className="assign-form-group">
                  <label className="assign-label">Staff</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select
                      className="assign-input"
                      style={{ flex: 1 }}
                      value={selectedStaffDraft}
                      disabled={staffOptions.length === 0 || (isSingleManager && isSourceStaffLoading)}
                      onChange={(e) => setSingleModal((prev) => prev ? { ...prev, staffDraft: e.target.value } : null)}
                    >
                      <option value="">
                        {staffOptions.length > 0
                          ? 'Add staff member...'
                          : 'No staff available'}
                      </option>
                      {staffOptions.map((s) => {
                        const freeCapacity = formatFreeCapacity(s.freeValue, dollarMode)
                        return (
                          <option key={s.staffId} value={s.staffId}>
                            {freeCapacity ? `${s.staffName} - ${freeCapacity}` : s.staffName}
                          </option>
                        )
                      })}
                    </select>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      style={{ whiteSpace: 'nowrap' }}
                      disabled={!selectedStaffDraft || (isSingleManager && isSourceStaffLoading)}
                      onClick={() => {
                        if (isSingleManager && singleModal.staffIds.length > 0) {
                          addToast(SINGLE_MANAGER_MESSAGE, 'error')
                          return
                        }
                        setSingleModal((prev) => {
                          if (
                            !prev
                            || !prev.staffDraft
                            || prev.staffIds.includes(prev.staffDraft)
                            || !staffOptions.some((s) => s.staffId === prev.staffDraft)
                          ) return prev
                          return { ...prev, staffIds: [...prev.staffIds, prev.staffDraft], staffDraft: '' }
                        })
                      }}
                    >+ Add</button>
                  </div>
                </div>

                <div className="assign-form-group">
                  <div className="alloc-hours-heading-row">
                    <label className="assign-label">{metricInputLabel} - Staff x {periodUnit}</label>
                    <span className="alloc-hours-budget-note">
                      Budget: {formatMetricValue(targetValue, dollarMode)}
                    </span>
                  </div>
                  <div
                    className={`alloc-hours-table-wrap${isSourceStaffLoading ? ' is-loading' : ''}`}
                    aria-busy={isSourceStaffLoading}
                  >
                    <table className="alloc-hours-table">
                      <thead>
                        <tr>
                          <th className="alloc-hours-th-name"></th>
                          {periods.map((period) => (
                            <th key={period} className="alloc-hours-th">
                              {formatAllocationPeriodLabel(period, granularity)}
                            </th>
                          ))}
                          <th className="alloc-hours-th alloc-hours-th-total">Total</th>
                          <th style={{ width: 30 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {singleModal.staffIds.length === 0 && (
                          <tr className="alloc-hours-empty-row">
                            <td className="alloc-hours-empty-cell" colSpan={periods.length + 3}>
                              Adjust the {periodUnit.toLowerCase()}s first or add a staff member to start allocating.
                            </td>
                          </tr>
                        )}
                        {singleModal.staffIds.map((staffId) => {
                          const staffName = staffSummaries.find((s) => s.staffId === staffId)?.staffName
                            ?? displayStaffDirectory.find((s) => s.staffId === staffId)?.staffName
                            ?? staffId
                          const rowTotal = periods.reduce((sum, period) => sum + (singleModal.grid[staffId]?.[period] ?? 0), 0)
                          const sourceUnscheduledValue = dollarMode
                            ? sourceAllocationStaffById[staffId]?.unscheduledAmount
                            : sourceAllocationStaffById[staffId]?.unscheduledHours
                          const showUnscheduledValue = job.allocationStatus === 'unscheduled'
                            && sourceUnscheduledValue != null
                            && sourceUnscheduledValue > 0
                          const remainingUnscheduledValue = showUnscheduledValue
                            ? sourceUnscheduledValue - rowTotal
                            : null
                          return (
                            <tr key={staffId} className="alloc-hours-row">
                              <td className="alloc-hours-name">
                                <div>{staffName}</div>
                                {remainingUnscheduledValue != null && (
                                  <div className={`alloc-hours-staff-note${remainingUnscheduledValue < 0 ? ' is-over' : ''}`}>
                                    Unscheduled {dollarMode ? 'Amount' : 'Hrs'} left: {formatUnscheduledValueLeft(remainingUnscheduledValue, dollarMode)}
                                  </div>
                                )}
                              </td>
                              {periods.map((period) => {
                                const cell = validation.cellStates[staffId]?.[period] ?? createEmptyCellState()
                                const cellIsInvalid = !dollarMode && cell.isInvalid
                                return (
                                  <td key={period} className="alloc-hours-cell">
                                    <div className={`alloc-hours-cell-wrap${cellIsInvalid ? ' is-invalid' : ''}`}>
                                      <input
                                        type="number"
                                        value={singleModal.grid[staffId]?.[period] ?? ''}
                                        min={0}
                                        max={999}
                                        className={`alloc-hours-input-cell${cellIsInvalid ? ' is-invalid' : ''}`}
                                        onChange={(e) => {
                                          setSingleModal((prev) => !prev ? null : {
                                            ...prev,
                                            grid: updateGridValue(prev.grid, staffId, period, e.target.value),
                                          })
                                        }}
                                      />
                                      {!dollarMode && (
                                        <div className={`alloc-hours-cell-note${cellIsInvalid ? ' is-invalid' : ''}`}>
                                          {cell.enteredValue > 0
                                            ? `Left ${formatSignedMetricValue(cell.remainingAfterEntry, dollarMode)}`
                                            : `Avail ${formatMetricValue(cell.availableForPeriod, dollarMode)}`}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                )
                              })}
                              <td className="alloc-hours-row-total">{formatMetricValue(rowTotal, dollarMode)}</td>
                              <td className="alloc-hours-remove">
                                <button
                                  type="button"
                                  onClick={() => setSingleModal((prev) => {
                                    if (!prev) return null
                                    const { [staffId]: _removed, ...restGrid } = prev.grid
                                    return { ...prev, staffIds: prev.staffIds.filter((id) => id !== staffId), grid: restGrid }
                                  })}
                                >&times;</button>
                              </td>
                            </tr>
                          )
                        })}
                        <tr className="alloc-hours-totals-row">
                          <td>Total</td>
                          {periods.map((period) => {
                            const periodTotal = singleModal.staffIds.reduce((sum, sid) => sum + (singleModal.grid[sid]?.[period] ?? 0), 0)
                            return <td key={period}>{formatMetricValue(periodTotal, dollarMode)}</td>
                          })}
                          <td className="alloc-hours-grand-total">{formatMetricValue(totalAllocated, dollarMode)}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                    {isSourceStaffLoading && (
                      <div className="alloc-hours-table-loading" aria-label="Loading source staff allocations">
                        <Spin size="small" />
                      </div>
                    )}
                  </div>
                  <div className="alloc-hours-table-footer">
                    <div className="alloc-hours-period-actions">
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ fontSize: 11 }}
                        disabled={singleModal.periodCount <= MIN_PERIOD_COUNT}
                        onClick={() => setSingleModal((prev) => {
                          if (!prev) return null
                          const nextPeriodCount = Math.max(MIN_PERIOD_COUNT, prev.periodCount - 1)
                          const nextPeriods = buildPeriodStarts(prev.periodStart, nextPeriodCount, granularity)
                          return {
                            ...prev,
                            periodCount: nextPeriodCount,
                            grid: pruneGridToPeriods(prev.grid, nextPeriods),
                          }
                        })}
                      >- Remove {periodUnit}</button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ fontSize: 11 }}
                        onClick={() => setSingleModal((prev) => prev ? { ...prev, periodCount: prev.periodCount + 1 } : null)}
                      >+ Add {periodUnit}</button>
                    </div>
                    <span className="alloc-hours-budget-note">
                      Allocated: {formatMetricValue(totalAllocated, dollarMode)}
                    </span>
                  </div>
                  {!dollarMode && validation.hasInvalid && (
                    <Alert
                      style={{ marginTop: 12 }}
                      type="warning"
                      showIcon
                      message="Some entries exceed the selected staff member's available capacity for the chosen week or month. You can still save this allocation."
                    />
                  )}
                  {isBudgetExceeded && (
                    <Alert
                      style={{ marginTop: 12 }}
                      type="warning"
                      showIcon
                      message={`Total allocation of ${formatMetricValue(totalAllocated, dollarMode)} exceeds the job budget of ${formatMetricValue(targetValue, dollarMode)}. You can still save this allocation.`}
                    />
                  )}
                </div>

                <div className="assign-form-group">
                  <label className="assign-label">Notes (optional)</label>
                  <textarea
                    className="assign-input allocation-notes"
                    rows={2}
                    placeholder="E.g., Priya on GST calcs, Mark on lodgement prep..."
                    value={singleModal.notes}
                    onChange={(e) => setSingleModal((prev) => prev ? { ...prev, notes: e.target.value } : null)}
                  />
                </div>
              </div>
              <div className="allocation-modal-footer">
                <button type="button" className="btn" disabled={isSingleAllocationSaving} onClick={closeSingleModal}>Cancel</button>
                <Button
                  type="primary"
                  loading={isSingleAllocationSaving}
                  onClick={saveSingle}
                >
                  {singleModal.mode === 'edit' ? 'Save Changes' : 'Allocate'}
                </Button>
              </div>
            </div>
          </div>
        )
      })()}

      {bulkModal && (() => {
        const bulkJobs = jobs.filter((j) => bulkModal.jobIds.includes(j.jobId))
        const periods = buildPeriodStarts(bulkModal.periodStart, bulkModal.periodCount, granularity)
        const staffSummaries = summarizeStaffAvailabilityForPeriods(selectableStaffDirectory, periods, availabilityByPeriod, dollarMode)
        const selectedStaffSummary = staffSummaries.find((s) => s.staffId === bulkModal.staffId)
        const validation = getBulkModalValidation(bulkModal, periods, availabilityByPeriod, dollarMode)
        const bulkBudgetTolerance = dollarMode ? 0.5 : 0.05
        const overBudgetJobIds = bulkJobs
          .filter((job) => {
            const rowTotal = periods.reduce((sum, period) => sum + (bulkModal.grid[job.jobId]?.[period] ?? 0), 0)
            const targetValue = getTargetValue(job, visibleBudgetValues[job.jobId], dollarMode)
            return rowTotal > targetValue + bulkBudgetTolerance
          })
          .map((j) => j.jobId)
        const hasBulkBudgetExceeded = overBudgetJobIds.length > 0
        const grandTotal = bulkJobs.reduce(
          (sum, job) => sum + periods.reduce((s, period) => s + (bulkModal.grid[job.jobId]?.[period] ?? 0), 0),
          0
        )
        return (
          <div className="allocation-modal-overlay" onClick={closeBulkModal}>
            <div
              className="allocation-modal"
              style={{ maxWidth: 680 }}
              role="dialog"
              aria-modal="true"
              aria-label="Bulk allocate jobs"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="allocation-modal-header">
                <div>
                  <div className="allocation-modal-title">Bulk Allocate</div>
                  <div className="allocation-modal-subtitle">{bulkModal.jobIds.length} jobs selected</div>
                </div>
                <button type="button" className="allocation-modal-close" disabled={isBulkAllocationSaving} onClick={closeBulkModal} aria-label="Close">&times;</button>
              </div>
              <div className="allocation-modal-body">
                <div className="assign-form-group">
                  <label className="assign-label">Assign to</label>
                  <select
                    className="assign-input"
                    value={bulkModal.staffId}
                    onChange={(e) => setBulkModal((prev) => prev ? loadBulkStaffAllocations(prev, e.target.value) : null)}
                  >
                    <option value="">Select staff member...</option>
                    {staffSummaries.map((s) => {
                      const freeCapacity = formatFreeCapacity(s.freeValue, dollarMode)
                      return (
                        <option key={s.staffId} value={s.staffId}>
                          {freeCapacity ? `${s.staffName} - ${freeCapacity}` : s.staffName}
                        </option>
                      )
                    })}
                  </select>
                  {selectedStaffSummary && (
                    <div className="alloc-hours-budget-note" style={{ marginTop: 6 }}>
                      Visible capacity: {formatFreeCapacity(selectedStaffSummary.freeValue, dollarMode)}
                    </div>
                  )}
                </div>

                <div className="assign-form-group">
                  <label className="assign-label">{metricInputLabel} - Job x {periodUnit}</label>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="alloc-hours-table">
                      <thead>
                        <tr>
                          <th className="alloc-hours-th-name alloc-hours-th-job">Job</th>
                          {periods.map((period) => (
                            <th key={period} className="alloc-hours-th">
                              {formatAllocationPeriodLabel(period, granularity)}
                            </th>
                          ))}
                          <th className="alloc-hours-th alloc-hours-th-total">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bulkJobs.map((job) => {
                          const rowTotal = periods.reduce((sum, period) => sum + (bulkModal.grid[job.jobId]?.[period] ?? 0), 0)
                          const targetValue = getTargetValue(job, visibleBudgetValues[job.jobId], dollarMode)
                          return (
                            <tr key={job.jobId} className="alloc-hours-row">
                              <td className="alloc-hours-job-cell">
                                <strong>{job.clientName}</strong><br />
                                <span className="alloc-hours-job-sub">{job.jobName} &middot; {formatMetricValue(targetValue, dollarMode)}</span>
                              </td>
                              {periods.map((period) => {
                                const cell = validation.cellStates[job.jobId]?.[period] ?? createEmptyCellState()
                                const cellIsInvalid = !dollarMode && cell.isInvalid
                                return (
                                  <td key={period} className="alloc-hours-cell">
                                    <div className={`alloc-hours-cell-wrap${cellIsInvalid ? ' is-invalid' : ''}`}>
                                      <input
                                        type="number"
                                        value={bulkModal.grid[job.jobId]?.[period] ?? ''}
                                        min={0}
                                        max={999}
                                        className={`alloc-hours-input-cell${cellIsInvalid ? ' is-invalid' : ''}`}
                                        onChange={(e) => {
                                          const value = Math.max(0, Number(e.target.value) || 0)
                                          setBulkModal((prev) => !prev ? null : {
                                            ...prev,
                                            grid: { ...prev.grid, [job.jobId]: { ...(prev.grid[job.jobId] ?? {}), [period]: value } },
                                          })
                                        }}
                                      />
                                      {!dollarMode && (
                                        <div className={`alloc-hours-cell-note${cellIsInvalid ? ' is-invalid' : ''}`}>
                                          {cell.enteredValue > 0
                                            ? `Left ${formatSignedMetricValue(cell.remainingAfterEntry, dollarMode)}`
                                            : `Avail ${formatMetricValue(cell.availableForPeriod, dollarMode)}`}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                )
                              })}
                              <td className="alloc-hours-row-total">{formatMetricValue(rowTotal, dollarMode)}</td>
                            </tr>
                          )
                        })}
                        <tr className="alloc-hours-totals-row">
                          <td>Total per {periodUnit.toLowerCase()}</td>
                          {periods.map((period) => {
                            const periodTotal = bulkJobs.reduce((sum, job) => sum + (bulkModal.grid[job.jobId]?.[period] ?? 0), 0)
                            return <td key={period}>{formatMetricValue(periodTotal, dollarMode)}</td>
                          })}
                          <td className="alloc-hours-grand-total">{formatMetricValue(grandTotal, dollarMode)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="alloc-hours-table-footer">
                    <div className="alloc-hours-period-actions">
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ fontSize: 11 }}
                        disabled={bulkModal.periodCount <= MIN_PERIOD_COUNT}
                        onClick={() => setBulkModal((prev) => {
                          if (!prev) return null
                          const nextPeriodCount = Math.max(MIN_PERIOD_COUNT, prev.periodCount - 1)
                          const nextPeriods = buildPeriodStarts(prev.periodStart, nextPeriodCount, granularity)
                          return {
                            ...prev,
                            periodCount: nextPeriodCount,
                            grid: pruneGridToPeriods(prev.grid, nextPeriods),
                          }
                        })}
                      >- Remove {periodUnit}</button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ fontSize: 11 }}
                        onClick={() => setBulkModal((prev) => prev ? { ...prev, periodCount: prev.periodCount + 1 } : null)}
                      >+ Add {periodUnit}</button>
                    </div>
                  </div>
                  {!dollarMode && validation.hasInvalid && (
                    <Alert
                      style={{ marginTop: 12 }}
                      type="warning"
                      showIcon
                      message="The entered allocations exceed the selected staff member's available capacity for one or more periods. You can still save these allocations."
                    />
                  )}
                  {hasBulkBudgetExceeded && (
                    <Alert
                      style={{ marginTop: 12 }}
                      type="error"
                      showIcon
                      message={`${overBudgetJobIds.length === 1 ? '1 job exceeds' : `${overBudgetJobIds.length} jobs exceed`} their budget. You can still save these allocations.`}
                    />
                  )}
                </div>

              </div>
              <div className="allocation-modal-footer">
                <button type="button" className="btn" disabled={isBulkAllocationSaving} onClick={closeBulkModal}>Cancel</button>
                <Button type="primary" loading={isBulkAllocationSaving} onClick={saveBulk}>Allocate All</Button>
              </div>
            </div>
          </div>
        )
      })()}

      {budgetReviewModal && (
        <div
          className="allocation-modal-overlay"
          onClick={isBudgetReviewSaving ? undefined : closeBudgetReviewModal}
        >
          <div
            className="allocation-modal"
            style={{ maxWidth: 640 }}
            role="dialog"
            aria-modal="true"
            aria-label="Save budget changes"
            aria-busy={isBudgetReviewSaving}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="allocation-modal-header">
              <div>
                <div className="allocation-modal-title">Save Budget Changes</div>
                <div className="allocation-modal-subtitle">
                  {reviewBudgetChanges.length} {reviewBudgetChanges.length === 1 ? 'job' : 'jobs'} modified
                </div>
              </div>
              <button
                type="button"
                className="allocation-modal-close"
                disabled={isBudgetReviewSaving}
                onClick={closeBudgetReviewModal}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div className="allocation-modal-body">
              {budgetReviewError && (
                <Alert
                  style={{ marginBottom: 16 }}
                  type="error"
                  showIcon
                  message={budgetReviewError}
                />
              )}
              <div className="budget-review-table-wrap">
                <table className="budget-review-table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Job</th>
                      <th>Current Budget</th>
                      <th>New Budget</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewBudgetChanges.map((change) => (
                      <tr key={change.job.jobId}>
                        <td><strong>{change.job.clientName}</strong></td>
                        <td title={change.job.jobName}>{change.job.jobName}</td>
                        <td>{formatMetricValue(change.currentValue, dollarMode)}</td>
                        <td><strong>{formatMetricValue(change.newValue, dollarMode)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="allocation-modal-footer">
              <button
                type="button"
                className="btn"
                disabled={isBudgetReviewSaving}
                onClick={closeBudgetReviewModal}
              >
                Cancel
              </button>
              <Button
                type="primary"
                loading={isBudgetReviewSaving}
                onClick={saveBudgetChanges}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function buildBulkAllocationPayload(
  modal: BulkModal,
  periods: string[],
  granularity: Granularity,
  dollarMode: boolean
): SaveAllocationPayload[] {
  return modal.jobIds.flatMap((jobId) =>
    buildPeriodAllocationEntries(periods, jobId, modal.grid, granularity)
      .map((entry) => ({
        jobId,
        staffId: modal.staffId,
        weekStart: entry.weekStart,
        allocatedHours: dollarMode ? undefined : entry.allocatedValue,
        allocatedAmount: dollarMode ? entry.allocatedValue : undefined,
      }))
  )
}

function getChangedBudgets(
  jobs: AllocationJob[],
  planningValues: Record<string, number>,
  savedBudgetValues: Record<string, number>,
  dollarMode: boolean
): BudgetChange[] {
  const tolerance = dollarMode ? 0.005 : 0.0005

  return jobs
    .map((job) => {
      if (!Object.prototype.hasOwnProperty.call(planningValues, job.jobId)) {
        return null
      }

      const newValue = Number(planningValues[job.jobId] ?? 0)
      const currentValue = Number(savedBudgetValues[job.jobId] ?? getBudgetValue(job, dollarMode) ?? 0)
      if (Math.abs(newValue - currentValue) <= tolerance) {
        return null
      }

      return { job, currentValue, newValue }
    })
    .filter((change): change is BudgetChange => Boolean(change))
}

function buildPeriodAllocationEntries(
  periodStarts: string[],
  entityId: string,
  grid: Record<string, Record<string, number>>,
  granularity: Granularity
): { weekStart: string; periodStart: string; allocatedValue: number }[] {
  return periodStarts.flatMap((period) => {
    const periodValue = grid[entityId]?.[period] ?? 0
    if (periodValue <= 0) return []
    if (granularity === 'monthly') {
      return [{ weekStart: period, periodStart: period, allocatedValue: periodValue }]
    }

    const weeks = getWeeksInPeriod(period, granularity)
    const base = Math.floor((periodValue / weeks.length) * 10) / 10
    const remainder = Math.round((periodValue - base * weeks.length) * 10) / 10
    return weeks.map((weekStart, i) => ({
      weekStart,
      periodStart: period,
      allocatedValue: i === weeks.length - 1 ? Math.round((base + remainder) * 10) / 10 : base,
    }))
  })
}

type AvailabilitySummary = {
  staffId: string
  staffName: string
}

type CellValidationState = {
  availableForPeriod: number
  enteredValue: number
  remainingAfterEntry: number
  isInvalid: boolean
}

type ValidationResult = {
  hasInvalid: boolean
  cellStates: Record<string, Record<string, CellValidationState>>
}

type AllocationJobGroup = {
  jobId: string
  clientName: string
  jobName: string
  staffNames: string[]
  weekStart: string
  weekEnd: string
  periodStarts: string[]
  totalAllocated: number
  allocations: JobAllocation[]
}

function groupAllocationsByJob(allocations: JobAllocation[], granularity: Granularity, dollarMode: boolean): AllocationJobGroup[] {
  const groups = allocations.reduce<Record<string, AllocationJobGroup>>((acc, allocation) => {
    const value = dollarMode ? allocation.allocatedAmount ?? 0 : allocation.allocatedHours ?? 0
    const existing = acc[allocation.jobId]
    if (!existing) {
      acc[allocation.jobId] = {
        jobId: allocation.jobId,
        clientName: allocation.clientName,
        jobName: allocation.jobName,
        staffNames: [allocation.staffName],
        weekStart: allocation.weekStart,
        weekEnd: allocation.weekStart,
        periodStarts: [],
        totalAllocated: value,
        allocations: [allocation],
      }
      return acc
    }

    existing.totalAllocated += value
    existing.allocations.push(allocation)
    if (!existing.staffNames.includes(allocation.staffName)) {
      existing.staffNames.push(allocation.staffName)
    }
    if (allocation.weekStart < existing.weekStart) {
      existing.weekStart = allocation.weekStart
    }
    if (allocation.weekStart > existing.weekEnd) {
      existing.weekEnd = allocation.weekStart
    }
    return acc
  }, {})

  return Object.values(groups)
    .map((group) => ({
      ...group,
      staffNames: group.staffNames.sort((a, b) => a.localeCompare(b)),
      periodStarts: Array.from(new Set(group.allocations.map((allocation) => normalizePeriodStartIso(allocation.weekStart, granularity)))).sort(),
    }))
    .sort((a, b) => a.clientName.localeCompare(b.clientName) || a.jobName.localeCompare(b.jobName))
}

function groupAllocationsByStaffPeriod(
  allocations: JobAllocation[],
  granularity: Granularity,
  dollarMode: boolean
) {
  return allocations.reduce<Record<string, Record<string, number>>>((acc, allocation) => {
    const period = normalizePeriodStartIso(allocation.weekStart, granularity)
    const value = dollarMode ? allocation.allocatedAmount ?? 0 : allocation.allocatedHours ?? 0
    const staffGrid = acc[allocation.staffId] ?? {}
    staffGrid[period] = (staffGrid[period] ?? 0) + value
    acc[allocation.staffId] = staffGrid
    return acc
  }, {})
}

function groupAllocationsByJobPeriod(
  allocations: JobAllocation[],
  granularity: Granularity,
  dollarMode: boolean
) {
  return allocations.reduce<Record<string, Record<string, number>>>((acc, allocation) => {
    const period = normalizePeriodStartIso(allocation.weekStart, granularity)
    const value = dollarMode ? allocation.allocatedAmount ?? 0 : allocation.allocatedHours ?? 0
    const jobGrid = acc[allocation.jobId] ?? {}
    jobGrid[period] = (jobGrid[period] ?? 0) + value
    acc[allocation.jobId] = jobGrid
    return acc
  }, {})
}

function getInclusivePeriodCount(fromIso: string, toIso: string, granularity: Granularity) {
  const from = new Date(`${normalizePeriodStartIso(fromIso, granularity)}T00:00:00`)
  const to = new Date(`${normalizePeriodStartIso(toIso, granularity)}T00:00:00`)

  if (granularity === 'monthly') {
    return Math.max(1, (to.getFullYear() - from.getFullYear()) * 12 + to.getMonth() - from.getMonth() + 1)
  }

  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000)
  return Math.max(1, Math.floor(days / 7) + 1)
}

function getJobFromAllocationGroup(group: AllocationJobGroup | undefined): AllocationJob | undefined {
  if (!group) return undefined

  return {
    jobId: group.jobId,
    clientName: group.clientName,
    jobName: group.jobName,
    budgetHours: null,
    usedHours: 0,
    budgetAmount: null,
    usedAmount: 0,
    progressPct: 0,
    dueDate: null,
    status: 'on-track',
    isRecurring: false,
    state: '',
    staff: [],
    allocatedHours: 0,
    remainingHours: group.totalAllocated,
    allocatedAmount: 0,
    remainingAmount: group.totalAllocated,
    allocationStatus: 'needs-reallocation',
    notes: null,
  }
}

function getSingleModalTargetValue(
  modal: SingleModal,
  job: AllocationJob | undefined,
  group: AllocationJobGroup | undefined,
  planningValue: number | null | undefined,
  dollarMode: boolean
) {
  if (modal.mode === 'create') {
    return getTargetValue(job, planningValue, dollarMode)
  }

  if (!job) return planningValue ?? group?.totalAllocated ?? 0

  if (planningValue != null) return planningValue

  const totalFromJob = dollarMode
    ? (job.allocatedAmount ?? 0) + (job.remainingAmount ?? 0)
    : (job.allocatedHours ?? 0) + (job.remainingHours ?? 0)
  const budgetValue = dollarMode
    ? job.budgetAmount ?? job.planningAmount
    : job.budgetHours ?? job.planningHours

  return budgetValue ?? (totalFromJob > 0 ? totalFromJob : group?.totalAllocated ?? 0)
}

function summarizeStaffDirectory(availability: StaffAvailability[]) {
  return Object.values(
    availability.reduce<Record<string, AvailabilitySummary>>((acc, item) => {
      acc[item.staffId] ??= { staffId: item.staffId, staffName: item.staffName }
      return acc
    }, {})
  ).sort((a, b) => a.staffName.localeCompare(b.staffName))
}

function mergeSelectableStaffDirectory(
  staffDirectory: AvailabilitySummary[],
  sourceStaff: SourceAllocationStaff[] = []
) {
  const merged = staffDirectory.reduce<Record<string, AvailabilitySummary>>((acc, staff) => {
    acc[staff.staffId] = staff
    return acc
  }, {})

  for (const staff of sourceStaff) {
    if (!staff.staffId) continue
    merged[staff.staffId] ??= {
      staffId: staff.staffId,
      staffName: staff.staffName || staff.staffId,
    }
  }

  return Object.values(merged).sort((a, b) => a.staffName.localeCompare(b.staffName))
}

function mergeAllocationDisplayDirectory(
  selectableStaffDirectory: AvailabilitySummary[],
  allocations: JobAllocation[]
) {
  const merged = selectableStaffDirectory.reduce<Record<string, AvailabilitySummary>>((acc, staff) => {
    acc[staff.staffId] = staff
    return acc
  }, {})

  for (const allocation of allocations) {
    merged[allocation.staffId] ??= {
      staffId: allocation.staffId,
      staffName: allocation.staffName,
    }
  }

  return Object.values(merged).sort((a, b) => a.staffName.localeCompare(b.staffName))
}

function summarizeStaffAvailabilityForPeriods(
  staffDirectory: AvailabilitySummary[],
  periods: string[],
  availabilityByPeriod: Record<string, StaffAvailability>,
  dollarMode: boolean
) {
  return staffDirectory
    .map((staff) => ({
      staffId: staff.staffId,
      staffName: staff.staffName,
      freeValue: periods.reduce(
        (sum, period) => sum + (
          getFreeCapacityValue(availabilityByPeriod[getAvailabilityKey(staff.staffId, period)], dollarMode)
        ),
        0
      ),
    }))
    .sort((a, b) => b.freeValue - a.freeValue)
}

function getTargetValue(job: AllocationJob | undefined, planningValue: number | null | undefined, dollarMode: boolean) {
  if (!job) return planningValue ?? 0
  return planningValue ?? (dollarMode
    ? job.remainingAmount ?? job.planningAmount ?? job.budgetAmount ?? 0
    : job.remainingHours ?? job.planningHours ?? job.budgetHours ?? 0)
}

function getBudgetValue(job: AllocationJob, dollarMode: boolean) {
  return dollarMode ? job.budgetAmount : job.budgetHours
}

function getRemainingValue(job: AllocationJob, dollarMode: boolean) {
  return dollarMode ? job.remainingAmount : job.remainingHours
}

function getAllocatedValue(job: AllocationJob, dollarMode: boolean) {
  return dollarMode ? job.allocatedAmount : job.allocatedHours
}

function formatDate(value: string | null) {
  if (!value) return '-'
  return new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short' }).format(new Date(value))
}

function formatHours(value: number | null | undefined) {
  const num = Number(value ?? 0)
  return Number.isInteger(num) ? `${num}` : num.toFixed(1)
}

function formatCurrency(value: number | null | undefined) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(Number(value ?? 0))
}

function parseCurrencyInput(value: string | number | null | undefined) {
  const numeric = Number(String(value ?? '').replace(/[^\d.-]/g, ''))
  return Number.isFinite(numeric) ? numeric : 0
}

function formatMetricValue(value: number | null | undefined, dollarMode: boolean) {
  if (dollarMode) return formatCurrency(value)
  return `${formatHours(value)} hrs`
}

function formatRemainingValue(value: number, dollarMode: boolean) {
  if (dollarMode) return formatCurrency(value)
  return `${formatHours(value)} hrs`
}

function formatUnscheduledValueLeft(value: number, dollarMode: boolean) {
  const absoluteValue = Math.abs(value)
  return value < 0
    ? `${formatMetricValue(absoluteValue, dollarMode)} over`
    : formatMetricValue(absoluteValue, dollarMode)
}

function formatSignedMetricValue(value: number, dollarMode: boolean) {
  if (dollarMode) {
    const prefix = value > 0 ? '' : value < 0 ? '-' : ''
    return `${prefix}${formatCurrency(Math.abs(value))}`
  }

  const prefix = value > 0 ? '' : value < 0 ? '-' : ''
  return `${prefix}${formatHours(Math.abs(value))} hrs`
}

function formatFreeCapacity(value: number, dollarMode: boolean) {
  if (dollarMode && value <= 0) return null
  return `${formatMetricValue(Math.max(0, value), dollarMode)} free`
}

function formatAllocationStatus(status: AllocationStatus) {
  if (status === 'allocated') return 'Allocated'
  if (status === 'no-budget') return 'No Budget'
  if (status === 'unscheduled') return 'Unscheduled'
  if (status === 'needs-reallocation') return 'Needs Reallocation'
  return 'Unassigned'
}

function getAllocationBadge(status: AllocationStatus) {
  if (status === 'allocated') return 'badge-green'
  if (status === 'needs-reallocation') return 'badge-amber'
  if (status === 'no-budget') return 'badge-blue'
  if (status === 'unscheduled') return 'badge-amber'
  return 'badge-purple'
}

function buildAvailabilityByPeriod(availability: StaffAvailability[]) {
  return availability.reduce<Record<string, StaffAvailability>>((acc, item) => {
    acc[getAvailabilityKey(item.staffId, item.weekStart)] = item
    return acc
  }, {})
}

function getAvailabilityKey(staffId: string, weekStart: string) {
  return `${staffId}::${weekStart}`
}

function getSingleModalValidation(
  modal: SingleModal,
  periods: string[],
  availabilityByPeriod: Record<string, StaffAvailability>,
  dollarMode: boolean
): ValidationResult {
  const cellStates: Record<string, Record<string, CellValidationState>> = {}
  let hasInvalid = false

  for (const staffId of modal.staffIds) {
    cellStates[staffId] = {}
    for (const period of periods) {
      const enteredValue = modal.grid[staffId]?.[period] ?? 0
      const existingValue = modal.existingGrid[staffId]?.[period] ?? 0
      const availableForPeriod = getFreeCapacityValue(availabilityByPeriod[getAvailabilityKey(staffId, period)], dollarMode) + existingValue
      const remainingAfterEntry = availableForPeriod - enteredValue
      const tolerance = dollarMode ? 0.5 : 0.05
      const isInvalid = enteredValue > availableForPeriod + tolerance

      if (isInvalid) {
        hasInvalid = true
      }

      cellStates[staffId][period] = {
        availableForPeriod,
        enteredValue,
        remainingAfterEntry,
        isInvalid,
      }
    }
  }

  return { hasInvalid, cellStates }
}

function getBulkModalValidation(
  modal: BulkModal,
  periods: string[],
  availabilityByPeriod: Record<string, StaffAvailability>,
  dollarMode: boolean
): ValidationResult {
  const cellStates: Record<string, Record<string, CellValidationState>> = {}
  if (!modal.staffId) {
    return { hasInvalid: false, cellStates }
  }

  const periodTotals = periods.reduce<Record<string, number>>((acc, period) => {
    acc[period] = modal.jobIds.reduce((sum, jobId) => sum + (modal.grid[jobId]?.[period] ?? 0), 0)
    return acc
  }, {})

  let hasInvalid = false

  for (const jobId of modal.jobIds) {
    cellStates[jobId] = {}
    for (const period of periods) {
      const enteredValue = modal.grid[jobId]?.[period] ?? 0
      const totalForPeriod = periodTotals[period] ?? 0
      const freeValue = getFreeCapacityValue(availabilityByPeriod[getAvailabilityKey(modal.staffId, period)], dollarMode)
      const availableForPeriod = Math.max(0, freeValue - (totalForPeriod - enteredValue))
      const remainingAfterEntry = freeValue - totalForPeriod
      const tolerance = dollarMode ? 0.5 : 0.05
      const isInvalid = totalForPeriod > freeValue + tolerance

      if (isInvalid) {
        hasInvalid = true
      }

      cellStates[jobId][period] = {
        availableForPeriod,
        enteredValue,
        remainingAfterEntry,
        isInvalid,
      }
    }
  }

  return { hasInvalid, cellStates }
}

function createEmptyCellState(): CellValidationState {
  return {
    availableForPeriod: 0,
    enteredValue: 0,
    remainingAfterEntry: 0,
    isInvalid: false,
  }
}

function pruneGridToPeriods<T>(
  grid: Record<string, Record<string, T>>,
  allowedPeriods: string[]
) {
  const allowed = new Set(allowedPeriods)

  return Object.fromEntries(
    Object.entries(grid).map(([entityId, periodValues]) => [
      entityId,
      Object.fromEntries(
        Object.entries(periodValues).filter(([period]) => allowed.has(period))
      ),
    ])
  )
}

function updateGridValue(
  grid: Record<string, Record<string, number>>,
  entityId: string,
  period: string,
  rawValue: string
) {
  const nextEntityGrid = { ...(grid[entityId] ?? {}) }
  if (rawValue === '') {
    delete nextEntityGrid[period]
  } else {
    nextEntityGrid[period] = Math.max(0, Number(rawValue) || 0)
  }

  return {
    ...grid,
    [entityId]: nextEntityGrid,
  }
}

function getFreeCapacityValue(availability: StaffAvailability | undefined, dollarMode: boolean) {
  if (!availability) return 0
  return Math.max(0, dollarMode ? availability.freeAmount : availability.freeHours)
}

function isSourceStaffAssignedToTaskRemovalError(error: unknown) {
  return error instanceof Error && error.message === SOURCE_STAFF_ASSIGNED_TO_TASK_REMOVAL_MESSAGE
}
